/* Atelier storefront behaviour: fit-to-width wordmarks, header state and
   the hero wordmark that shrinks into the bar on scroll. */
(function () {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Horizon scrolls .page-wrapper instead of the document from 990px up
     (assets/scroll-container.js), so scroll offsets are read from there. */
  const squeeze = window.matchMedia('(min-width: 990px)');

  function scrollTop() {
    const wrapper = squeeze.matches && document.querySelector('.page-wrapper');
    return wrapper ? wrapper.scrollTop : window.scrollY;
  }

  /* ---------- fit-to-width text ----------
     Each [data-at-fit] holds one <span>. The span is measured at a 100px
     reference size and the heading is scaled so the span spans
     data-at-fit-width (a 0-1 share) of the heading's own width. The span's
     own transform is lifted while measuring so the shrink below can't skew
     it. */
  const REF = 100;

  function fit(el) {
    const span = el.firstElementChild;
    if (!span) return;
    const share = parseFloat(el.dataset.atFitWidth) || 1;
    const target = el.clientWidth * share;
    if (!target) return;
    const transform = span.style.transform;
    span.style.transform = 'none';
    el.style.fontSize = REF + 'px';
    const natural = span.getBoundingClientRect().width;
    span.style.transform = transform;
    if (!natural) return;
    el.style.fontSize = (REF * target) / natural + 'px';
  }

  /* ---------- hero wordmark into the bar ----------
     The wordmark keeps scrolling with the page while it shrinks and drifts
     sideways, so it lands on the bar's wordmark at the moment its centre
     reaches the bar's centre line. The two then cross-fade. With reduced
     motion, or the hero setting off, the bar wordmark just waits until the
     big one has scrolled away. */
  let shrink = null;
  let heroObserver = null;

  function logoRect() {
    const link = document.querySelector('#header-group .header-logo');
    if (!link) return null;
    const shown = Array.from(link.children).find((child) => child.getBoundingClientRect().width > 0);
    return (shown || link).getBoundingClientRect();
  }

  function measureShrink() {
    if (!shrink) return;
    const span = shrink.mark.firstElementChild;
    const logo = logoRect();
    const header = document.querySelector('#header-component');
    if (!span || !logo || !header) {
      shrink.geo = null;
      return;
    }
    const transform = span.style.transform;
    span.style.transform = 'none';
    const r = span.getBoundingClientRect();
    span.style.transform = transform;
    const headerTop = header.getBoundingClientRect().top;
    shrink.geo = {
      span,
      x: r.left + r.width / 2,
      yDoc: r.top + scrollTop() + r.height / 2,
      w: r.width,
      logoX: logo.left + logo.width / 2,
      logoY: logo.top - headerTop + logo.height / 2,
      logoW: logo.width,
    };
  }

  function applyShrink() {
    const geo = shrink && shrink.geo;
    if (!geo || !geo.w) return;
    const travel = geo.yDoc - geo.logoY;
    if (travel <= 0) return;
    const p = Math.min(Math.max(scrollTop() / travel, 0), 1);
    const scale = 1 + (geo.logoW / geo.w - 1) * p;
    const dx = (geo.logoX - geo.x) * p;
    const handover = p >= 1 ? 1 : Math.min(Math.max((p - 0.9) / 0.1, 0), 1);
    geo.span.style.transform = `translate3d(${dx}px, 0, 0) scale(${scale})`;
    geo.span.style.opacity = String(1 - handover);
    root.style.setProperty('--at-logo-opacity', String(handover));
    root.classList.toggle('at-hide-header-logo', handover < 1);
  }

  function setupHero() {
    const previous = shrink && shrink.mark.firstElementChild;
    if (previous) {
      previous.style.transform = '';
      previous.style.opacity = '';
    }
    if (heroObserver) heroObserver.disconnect();
    heroObserver = null;
    shrink = null;
    root.style.removeProperty('--at-logo-opacity');
    root.classList.remove('at-hide-header-logo', 'at-logo-linked');

    const mark = document.querySelector('[data-at-hero-wordmark]');
    if (!mark) return;
    root.classList.add('at-hide-header-logo');

    if (mark.hasAttribute('data-at-shrink') && !reduceMotion.matches) {
      shrink = { mark, geo: null };
      root.classList.add('at-logo-linked');
      measureShrink();
      applyShrink();
    } else if ('IntersectionObserver' in window) {
      heroObserver = new IntersectionObserver(
        ([entry]) => root.classList.toggle('at-hide-header-logo', entry.isIntersecting),
        { threshold: 0 }
      );
      heroObserver.observe(mark);
    }
  }

  /* ---------- header ink and surface ----------
     The ink turns white while a [data-at-dark] section sits behind the
     middle of the bar. Once the page has scrolled and nothing dark is
     behind it, the bar takes the page colour so content passing underneath
     doesn't show through the navigation. */
  let darks = [];

  function updateHeader() {
    const bar = document.querySelector('#header-component .header__row--top');
    const box = bar ? bar.getBoundingClientRect() : { top: 0, height: 56 };
    const line = Math.max(box.top, 0) + box.height / 2;
    const dark = darks.some((el) => {
      const r = el.getBoundingClientRect();
      return r.top <= line && r.bottom > line;
    });
    root.classList.toggle('at-header-dark', dark);
    root.classList.toggle('at-header-solid', !dark && scrollTop() > 4);
  }

  /* ---------- wiring ---------- */
  let fitObserver = null;
  let queued = false;

  function frame() {
    queued = false;
    updateHeader();
    applyShrink();
  }

  function queueFrame() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(frame);
  }

  function relayout() {
    document.querySelectorAll('[data-at-fit]').forEach(fit);
    measureShrink();
    frame();
  }

  let relayoutFrame;
  const queueRelayout = () => {
    cancelAnimationFrame(relayoutFrame);
    relayoutFrame = requestAnimationFrame(relayout);
  };

  function init() {
    darks = Array.from(document.querySelectorAll('[data-at-dark]'));
    root.style.setProperty('--at-bar-bg', getComputedStyle(document.body).backgroundColor);

    if (fitObserver) fitObserver.disconnect();
    if ('ResizeObserver' in window) {
      fitObserver = new ResizeObserver(queueRelayout);
      document.querySelectorAll('[data-at-fit]').forEach((el) => fitObserver.observe(el));
    }

    document.querySelectorAll('[data-at-fit]').forEach(fit);
    setupHero();
    frame();
  }

  init();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(relayout);
  document.addEventListener('scroll', queueFrame, { passive: true, capture: true });
  window.addEventListener('resize', queueRelayout);
  reduceMotion.addEventListener('change', setupHero);

  /* The theme editor swaps section markup in place, so everything above
     re-binds to the new elements. */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(init));
})();
